'use client';

import type { DefaultQueryError } from '@ahm/api-wrappers-core';
import { toast } from '@ahm/react-ui/components/toast';
import {
  Anchor,
  Box,
  Image,
  Loader,
  Modal,
  SimpleGrid,
  Space,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { type ForgotPasscodeResponse, useForgotPasscodeMutation } from '../api';
import { getRemainCountDownTime } from '../lib/utils';
import { authLocales, createTranslations, type Locales } from '../locales';

interface ForgotPasscodeModalProps {
  opened: boolean;
  onClose: () => void;
  asPage?: boolean;
  isMobile?: boolean;
  mobile: string;
  initialData?: ForgotPasscodeResponse;
  lang?: keyof Locales;
}

export default function ForgotPasscodeModal({
  opened,
  onClose,
  isMobile = false,
  mobile,
  initialData,
  lang = 'vi',
}: ForgotPasscodeModalProps) {
  const [createdTimeResetPasscode, setCreatedTimeResetPasscode] = useState(0);
  const [countDownTimeResetPassCode, setCountDownTimeResetPasscode] = useState(
    getRemainCountDownTime('passcode')
  );
  const { mutate: forgotPassCode, status } = useForgotPasscodeMutation({});

  const tAuth = createTranslations({ locales: authLocales, lang });

  const handleResetPasscode = (
    isResend: boolean,
    initData?: ForgotPasscodeResponse
  ) => {
    const currentTime = Date.now() / 1000;
    const remainTime = getRemainCountDownTime('passcode');

    if (currentTime - createdTimeResetPasscode < remainTime) {
      return;
    }
    const params = {
      mobile,
    };

    if (initData) {
      if (!initData.is_sent && isResend === true) {
        return;
      }
      setCreatedTimeResetPasscode(initData.created_time);
      return;
    }

    forgotPassCode(params, {
      onSuccess: (data: ForgotPasscodeResponse) => {
        if (data) {
          if (!data.is_sent && isResend === true) {
            return;
          }
          setCreatedTimeResetPasscode(data.created_time);
        }
      },
      onError: (e: DefaultQueryError) => {
        const errorData = e?.response?.data;
        toast.error({
          title: errorData?.title,
          message: errorData?.description,
          withIcon: true,
        });
      },
    });
  };

  useEffect(() => {
    if (!createdTimeResetPasscode) return;
    const currentTime = Date.now() / 1000;

    let remainTime = getRemainCountDownTime('passcode');
    if (createdTimeResetPasscode) {
      if (
        currentTime - createdTimeResetPasscode <
        getRemainCountDownTime('passcode')
      ) {
        remainTime = Math.round(
          getRemainCountDownTime('passcode') -
            (currentTime - createdTimeResetPasscode)
        );
      } else {
        remainTime = 0;
      }
    }
    const resendPassCodeTimer = setInterval(() => {
      setCountDownTimeResetPasscode(remainTime);
      if (remainTime <= 0) {
        setCountDownTimeResetPasscode(0);
        if (resendPassCodeTimer) {
          clearInterval(resendPassCodeTimer);
        }
      }
    }, 1000);
    return () => clearInterval(resendPassCodeTimer);
  }, [countDownTimeResetPassCode, createdTimeResetPasscode]);

  useEffect(() => {
    if (initialData) {
      handleResetPasscode(false, initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={isMobile ? '100%' : rem(787)}
      fullScreen={isMobile}
      centered
      withCloseButton={true}
      closeButtonProps={{
        size: rem(36),
        style: {
          position: 'absolute',
          top: rem(8),
          color: 'var(--mantine-color-neutral-9)',
          ...(isMobile ? { left: rem(8) } : { right: rem(8) }),
        },
      }}
      styles={{
        header: {
          minHeight: 0,
          padding: 0,
        },
        body: {
          padding: 0,
        },
        overlay: {
          zIndex: 1500,
        },
        inner: {
          zIndex: 1500,
        },
      }}
    >
      <SimpleGrid cols={isMobile ? 1 : 2} spacing={0}>
        <Box
          bg={'rgb(254, 247, 232)'}
          w="100%"
          p={rem(24)}
          pt={{ base: rem(56), sm: rem(24) }}
          style={{
            aspectRatio: '332/321',
            overflow: 'hidden',
            borderBottomLeftRadius: isMobile ? '50%' : 0,
            borderBottomRightRadius: isMobile ? '50%' : 0,
          }}
        >
          <Image
            src="https://cdn-ahamoveimg.ahamove.com/web/auth/resend-otp.webp"
            alt="resend-otp"
          />
        </Box>
        <Box py={rem(32)} pr={rem(36)} pl="md">
          <Title order={2} mb="sm">
            {tAuth('checkYourPhone')}
          </Title>

          <Text c="neutral.6" fw={500} size="sm">
            {tAuth('resendPasscodeDescBefore')}
            <Text size="sm" fw={600} component="strong" c="neutral.6">
              +{mobile}
            </Text>{' '}
            {tAuth('resendPasscodeDescAfter')}
          </Text>

          <Space h={rem(isMobile ? 50 : 16)} w="100%" />

          <Stack align="flex-start" gap={0}>
            <Title order={5} c="neutral.7">
              {tAuth('didNotReceive')}
            </Title>
            {status === 'pending' ? (
              <Loader size="xs" mt={rem(4)} />
            ) : (
              <>
                {countDownTimeResetPassCode > 0 ? (
                  <Text fw={500} size="md" c="neutral.6">
                    {tAuth('resendLinkIn')}
                    <Title order={3} component="span" c="blue.5">
                      {countDownTimeResetPassCode}s
                    </Title>
                  </Text>
                ) : (
                  <Anchor
                    size="md"
                    mt={rem(4)}
                    fw={700}
                    onClick={() => handleResetPasscode(true)}
                  >
                    {tAuth('resendLink')}
                  </Anchor>
                )}
              </>
            )}
          </Stack>
        </Box>
      </SimpleGrid>
    </Modal>
  );
}
